import { Box, Button, HStack, Image, Menu, MenuButton, MenuItem, MenuList, Text, VStack } from '@chakra-ui/react';
import { MoreVertical } from 'lucide-react';
import iconsPng from '@/assets/img/png/icons';
import Markdown from '@/components/atoms/Markdown/Markdown';
import useFitText from '@/hooks/useFitText';
import { POST_IMAGE_OVERLAY } from '@/modules/content/common/postImageOverlay';

const POST_GRADIENT = 'linear-gradient(175deg, #4E1AEF 0%, #6718E2 38%, #AF578B 74%, #E67436 100%)';
const CLIP_FADE = 'linear-gradient(to bottom, black 90%, transparent 100%)';

type Props = {
  title: string;
  body: string;
  image?: string;
  authorName: string;
  authorPhotoUrl?: string | null;
};

const PostPreview = ({ title, body, image, authorName, authorPhotoUrl }: Props) => {
  const bodyFit = useFitText(body, { minFontSize: 14, maxFontSize: 26 });

  return (
    <Box position="relative" width="480px" maxWidth="100%" marginX="auto">
      <VStack
        align="stretch"
        position="relative"
        gap="16px"
        flexShrink={0}
        width="100%"
        aspectRatio="9 / 16"
        padding="32px"
        borderRadius="12px"
        overflow="hidden"
        boxShadow="0px 10px 28px rgba(14, 0, 98, 0.12)"
        backgroundImage={POST_GRADIENT}
      >
        {image && (
          <>
            <Image src={image} alt="" position="absolute" inset={0} width="100%" height="100%" objectFit="cover" />
            <Box position="absolute" inset={0} backgroundImage={POST_IMAGE_OVERLAY} pointerEvents="none" />
          </>
        )}
        <HStack gap="12px" minWidth={0} position="relative">
          <Box width="36px" height="36px" flexShrink={0} borderRadius="full" border="2px solid white" overflow="hidden">
            <Image
              src={authorPhotoUrl || '/svg/generic-user.svg'}
              alt=""
              width="100%"
              height="100%"
              objectFit="cover"
            />
          </Box>
          <Text color="white" fontSize="17px" fontWeight={600} noOfLines={1}>
            {authorName}
          </Text>
        </HStack>

        <Text
          color="white"
          fontFamily="Lora"
          fontStyle="italic"
          fontSize="24px"
          fontWeight={500}
          lineHeight="1.3"
          position="relative"
        >
          {title || 'Untitled'}
        </Text>

        <Box
          ref={bodyFit.containerRef}
          position="relative"
          flex="1 1 auto"
          minHeight={0}
          overflow="hidden"
          sx={bodyFit.isOverflowing ? { maskImage: CLIP_FADE, WebkitMaskImage: CLIP_FADE } : undefined}
        >
          <Box
            ref={bodyFit.contentRef}
            color="white"
            fontSize={`${bodyFit.fontSize}px`}
            fontWeight={600}
            lineHeight={1.22}
          >
            <Markdown
              color="inherit"
              fontSize="inherit"
              fontWeight="inherit"
              lineHeight="inherit"
              sx={{
                '& p, & li, & a, & h1, & h2, & h3': {
                  color: 'inherit',
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                  lineHeight: 'inherit',
                },
                '& p': { marginBottom: '0.5em' },
                '& a': { textDecoration: 'underline' },
              }}
            >
              {body || '_No content yet._'}
            </Markdown>
          </Box>
        </Box>
      </VStack>

      <VStack
        align="center"
        gap="16px"
        position={{ base: 'static', lg: 'absolute' }}
        left={{ lg: 'calc(100% + 24px)' }}
        top={{ lg: '50%' }}
        transform={{ lg: 'translateY(-50%)' }}
        marginTop={{ base: '16px', lg: 0 }}
        marginX={{ base: 'auto', lg: 0 }}
        width="48px"
      >
        {[
          { label: 'Like', icon: iconsPng.contentLike },
          { label: 'Save', icon: iconsPng.contentSave },
          { label: 'Share', icon: iconsPng.contentShare },
        ].map(({ label, icon }) => (
          <Button
            key={label}
            aria-label={label}
            isDisabled
            width="48px"
            height="48px"
            minWidth="48px"
            padding={0}
            borderRadius="full"
            backgroundColor="white"
            color="text.mediumBlue"
            boxShadow="0px 4px 12px 0px #00000040"
            _disabled={{ opacity: 1, cursor: 'default' }}
          >
            <Image src={icon} alt="" width="24px" height="24px" objectFit="contain" />
          </Button>
        ))}
        <Menu placement="bottom-start">
          <MenuButton
            as={Button}
            aria-label="More options"
            width="48px"
            height="40px"
            minWidth="48px"
            padding={0}
            variant="ghost"
            color="text.mediumBlue"
            flexDir={'column'}
            marginTop={'6px'}
            paddingTop={'8px'}
            _hover={{}}
          >
            <MoreVertical size={22} />
          </MenuButton>
          <MenuList
            minWidth="160px"
            padding="12px"
            border="none"
            borderRadius="8px"
            boxShadow="0px 8px 24px rgba(14, 0, 98, 0.14)"
          >
            {[
              { label: 'Report Content', color: 'extra.red' },
              { label: 'Share Content', color: 'text.mediumBlue' },
              { label: 'Not Interested', color: 'text.mediumBlue' },
            ].map(({ label, color }) => (
              <MenuItem
                key={label}
                isDisabled
                height="36px"
                paddingX="10px"
                color={color}
                fontSize="14px"
                fontWeight={600}
                _disabled={{ opacity: 1, cursor: 'default' }}
              >
                {label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </VStack>
    </Box>
  );
};

export default PostPreview;
